import React, { useState } from 'react';
import VoiceInputDebug from '../components/VoiceInputDebug';
import LexTester from '../components/LexTester';
import LexDebugger from '../components/LexDebugger';

/**
 * Debug page for testing voice input and Lex integration
 */
const DebugPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'voice' | 'lex' | 'lexdebug'>('voice');
  const [debugMessages, setDebugMessages] = useState<string[]>([]);
  
  // Add a debug message
  const addDebugMessage = (message: string) => {
    setDebugMessages(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };
  
  // Handle voice input result
  const handleVoiceResult = (text: string) => {
    addDebugMessage(`Voice input: "${text}"`);
  };
  
  return (
    <div className="debug-page" style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      <h1>Ivyron Debug Tools</h1>
      
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', borderBottom: '1px solid #dee2e6' }}>
          <button
            onClick={() => setActiveTab('voice')}
            style={{
              padding: '10px 20px',
              backgroundColor: activeTab === 'voice' ? '#f8f9fa' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'voice' ? '2px solid #007bff' : 'none',
              cursor: 'pointer'
            }}
          >
            Voice Debug
          </button>
          <button
            onClick={() => setActiveTab('lex')}
            style={{
              padding: '10px 20px',
              backgroundColor: activeTab === 'lex' ? '#f8f9fa' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'lex' ? '2px solid #007bff' : 'none',
              cursor: 'pointer'
            }}
          >
            Lex Tester
          </button>
          <button
            onClick={() => setActiveTab('lexdebug')}
            style={{
              padding: '10px 20px',
              backgroundColor: activeTab === 'lexdebug' ? '#f8f9fa' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'lexdebug' ? '2px solid #007bff' : 'none',
              cursor: 'pointer'
            }}
          >
            Lex Voice Debug
          </button>
        </div>
        
        <div style={{ padding: '20px 0' }}>
          {activeTab === 'voice' ? (
            <div>
              <h2>Voice Input Debugging</h2>
              <p>
                This tool helps debug voice recording issues by capturing audio and displaying detailed information.
                No Lex integration is used here to isolate audio recording problems.
              </p>
              <VoiceInputDebug onResult={handleVoiceResult} />
            </div>
          ) : activeTab === 'lex' ? (
            <div>
              <h2>Lex Integration Tester</h2>
              <p>
                This tool tests direct text integration with Amazon Lex to verify credentials and permissions.
                Use this to confirm your Lex bot is properly configured.
              </p>
              <LexTester />
            </div>
          ) : (
            <div>
              <h2>Lex Voice Integration Debugger</h2>
              <p>
                This advanced tool helps debug voice integration with Amazon Lex.
                It provides detailed information about the audio processing and Lex response.
              </p>
              <LexDebugger />
            </div>
          )}
        </div>
      </div>
      
      <div style={{ marginTop: '30px' }}>
        <h2>Debug Log</h2>
        <div
          style={{
            backgroundColor: '#f8f9fa',
            padding: '15px',
            borderRadius: '4px',
            border: '1px solid #dee2e6',
            height: '200px',
            overflowY: 'auto',
            fontFamily: 'monospace'
          }}
        >
          {debugMessages.length === 0 ? (
            <p style={{ color: '#6c757d' }}>No debug messages yet</p>
          ) : (
            debugMessages.map((message, index) => (
              <div key={index}>{message}</div>
            ))
          )}
        </div>
        <button
          onClick={() => setDebugMessages([])}
          style={{
            marginTop: '10px',
            padding: '5px 10px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Clear Log
        </button>
      </div>
      
      <div style={{ marginTop: '30px' }}>
        <h2>Troubleshooting Tips</h2>
        <ul>
          <li>Check browser console for detailed error messages</li>
          <li>Verify that your microphone is working properly</li>
          <li>Ensure your AWS credentials and permissions are correct</li>
          <li>Test with simple commands like "Turn on the lights"</li>
          <li>Try different browsers if issues persist</li>
          <li>Check network tab for API errors</li>
        </ul>
      </div>
    </div>
  );
};

export default DebugPage;